// FormStructureTab — the Data setup page's second tab: every FIELD of every
// form template, paged, over POST /dashboard-setup/fetch-structure.
//
// Its own file rather than a branch of DataSetupPage because it is its own
// entity with its own endpoint, pagination and column set — the TopicsTab /
// OptionSetsTab rule. It is mounted only while its tab is active, so its query
// needs no `skip`; it reports its total up through `onCount` for the tab badge.
//
// The table itself is read-only; the one write surface is the "Form structure"
// button, which opens the shared ExportSettingsDrawer over this tab's own
// warehouse export (form_structure_export). It is the same component the page's
// SQL settings button opens, handed a one-element section list — so it arrives
// open rather than closed.
//
// Otherwise deliberately bare. The design also draws a search box, a Filter
// control, sort glyphs on ten headers, row checkboxes and a "Manage sets"
// button — fetch-structure accepts `{ page, limit }` and nothing else, and the
// set-assignment endpoints are wired nowhere, so none of them are rendered.
// Each returns the moment its contract does. See DataSetupPage's header for the
// full list.
//
// Self-contained: its listing, its save, and its two CATALOGUE queries all live
// here (mounted only while active, so none needs a `skip`). The catalogues are
// LOOKUPS, not just label sources — a row carries a TITLE and the save sends an
// id, so until both resolve no cell value can be resolved and Save must not
// fire.
//
// `density` belongs to the PAGE, not to this tab: it is a view preference that
// means the same thing on both tables, so it has to survive a tab switch.
// Column visibility is the opposite — the two tabs share no column keys, so
// each owns its own.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Card, ColumnsMenu, DataTable, Icon, TableFooter } from '@/components/common';
import {
  FormMetadataType,
  useGetFormStructureQuery,
  useUpdateFieldMetadataMutation,
  type FieldMetadataPick,
  type FormStructureField,
} from '@/api/endpoints/dataSetupApi';
import {
  useGetQuestionSetSelectionQuery,
  type OptionSetSelection,
} from '@/api/endpoints/formSetsApi';
import { useGetTopicSelectionQuery } from '@/api/endpoints/topicSetsApi';
import useColumnVisibility from '@/hooks/useColumnVisibility';
import useStickyTotal from '@/hooks/useStickyTotal';
import useFormStructureColumns, {
  TOPIC_COLUMN_KEY,
  questionSetColumnKey,
} from './formStructureColumns';
import ExportSettingsDrawer from './ExportSettingsDrawer';
import ManageSetsModal from './ManageSetsModal';
import { FORM_STRUCTURE_SECTION } from './companyExportSections';
import { pageRange, saveErrorMsg, setsDelta, useTablePaging } from './dataSetupShared';
import DensityToggle, { type Density } from './DensityToggle';

/** Module constant, not an inline literal: the drawer reads its length once, on
 *  mount, to decide which section starts open. */
const FORM_STRUCTURE_SECTIONS = [FORM_STRUCTURE_SECTION];

interface FormStructureTabProps {
  /** Page-owned, so the choice survives switching tabs. */
  density: Density;
  onDensity: (next: Density) => void;
  /** Reports this tab's total up for its badge. */
  onCount?: (total: number) => void;
}

const FormStructureTab = ({ density, onDensity, onCount }: FormStructureTabProps) => {
  const { t } = useTranslation();

  const { page, limit, setPage, onLimit } = useTablePaging();
  const [exportOpen, setExportOpen] = useState(false);

  // The two catalogues — see the header for why they gate everything below.
  const {
    data: questionSets = [],
    isSuccess: questionSetsReady,
    isLoading: questionSetsLoading,
    isError: questionSetsError,
  } = useGetQuestionSetSelectionQuery();
  const { data: topics = [], isSuccess: topicsReady } = useGetTopicSelectionQuery();
  const cataloguesReady = questionSetsReady && topicsReady;
  // Touched cells only, keyed `${rowId}:${columnKey}` and holding an option ID.
  // Rows keep the titles the wire sent; resolution happens here.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [manageSetsOpen, setManageSetsOpen] = useState(false);
  // Which question sets are drawn as columns, and what the SERVER holds — the
  // baseline the added/removed delta is measured against.
  const [enabledSetIds, setEnabledSetIds] = useState<Set<string>>(() => new Set());
  const [savedSetIds, setSavedSetIds] = useState<Set<string>>(() => new Set());
  const [updateFieldMetadata, { isLoading: saving }] = useUpdateFieldMetadataMutation();

  const { data, currentData, isLoading, isFetching, isError } = useGetFormStructureQuery(
    { page, limit },
    { refetchOnMountOrArgChange: true },
  );

  const items = data?.items ?? [];
  // The API reports the record count on page-1 responses only (a defined 0 on
  // page 2+ here) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);
  const { from, to } = pageRange(page, limit, total);

  // This list takes no search and no filter, so the sticky total IS the true
  // entity count — it needs none of the unfiltered-only guarding the meta
  // tab's badge does.
  useEffect(() => {
    if (data) onCount?.(total);
  }, [data, total, onCount]);

  // Which question sets the server already materialised — the BASELINE, not the
  // column list. Read from the response's COLUMN KEYS (`setColumns`), not from
  // `setValues`: a selected set with no picks yet rides the wire as an all-null
  // column, which the value map (truthy strings only) rightly drops — presence
  // and value are different facts. The keys also carry the meta tab's form-set
  // columns; the catalogue intersection below is what screens those out.
  // Matching is case-insensitive because `sqlColumn` is only validated
  // lowercase for sets created in this app.
  const presentKeys = useMemo(() => new Set(data?.setColumns ?? []), [data]);
  const serverSets = useMemo(
    () => questionSets.filter((qs) => presentKeys.has(qs.sqlColumn.toLowerCase())),
    [questionSets, presentKeys],
  );

  // Seeded ONCE, from the first load that has both a response and a catalogue.
  // The meta tab reads its baseline from `headerFormSetIds`, which the server
  // states outright; this one is derived from the rows and so is page-DEPENDENT.
  // Recomputing it per page would let a page carrying fewer keys shrink the
  // baseline, and the difference would go out as `removedFormSetIds` — deleting
  // columns nobody asked to delete. It advances again only on a successful save.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !data || !cataloguesReady) return;
    seeded.current = true;
    const next = new Set(serverSets.map((qs) => qs.id));
    setSavedSetIds(next);
    setEnabledSetIds(next);
  }, [data, cataloguesReady, serverSets]);

  // Columns follow the USER's picks, not the response: a set just switched on
  // has no key on any row yet and still needs its column, or it could never be
  // given a first value. `addedFormSetIds` is what materialises it.
  const enabledSets = useMemo(
    () => questionSets.filter((qs) => enabledSetIds.has(qs.id)),
    [questionSets, enabledSetIds],
  );

  const applySets = useCallback((next: Set<string>, sets: OptionSetSelection[]) => {
    setEnabledSetIds(next);
    // A set switched off loses its column, so anything drafted against it would
    // be invisible and stale. NOTE the key shape: `${rowId}:dyn:<sqlColumn>` —
    // three segments, not the meta tab's two, so its `split(':')[1]` would read
    // the literal "dyn" here and prune nothing. Topic drafts always survive.
    const liveColumns = new Set(
      sets
        .filter((qs) => next.has(qs.id))
        .map((qs) => questionSetColumnKey(qs.sqlColumn.toLowerCase())),
    );
    setDrafts((d) =>
      Object.fromEntries(
        Object.entries(d).filter(([k]) => {
          const columnKey = k.slice(k.indexOf(':') + 1);
          return columnKey === TOPIC_COLUMN_KEY || liveColumns.has(columnKey);
        }),
      ),
    );
  }, []);

  // Title -> id, per catalogue. This is what turns a row's display value into
  // something saveable. A title with no entry resolves to '' and renders as the
  // placeholder; duplicate titles collapse, first occurrence winning — the
  // known cost of the wire sending titles rather than ids.
  const topicIdByTitle = useMemo(() => {
    const m = new Map<string, string>();
    for (const tp of topics) if (!m.has(tp.title)) m.set(tp.title, tp.id);
    return m;
  }, [topics]);
  const optionIdByValue = useMemo(() => {
    const m = new Map<string, Map<string, string>>();
    for (const qs of questionSets) {
      const byValue = new Map<string, string>();
      for (const o of qs.options) if (!byValue.has(o.value)) byValue.set(o.value, o.id);
      m.set(questionSetColumnKey(qs.sqlColumn.toLowerCase()), byValue);
    }
    return m;
  }, [questionSets]);

  /** What the SERVER holds for a cell, as an option id. */
  const savedValueOf = useCallback(
    (row: FormStructureField, columnKey: string): string => {
      if (columnKey === TOPIC_COLUMN_KEY)
        return row.topic ? (topicIdByTitle.get(row.topic) ?? '') : '';
      const sqlColumn = columnKey.slice('dyn:'.length);
      const title = row.setValues[sqlColumn];
      return title ? (optionIdByValue.get(columnKey)?.get(title) ?? '') : '';
    },
    [topicIdByTitle, optionIdByValue],
  );

  // A draft wins while it exists; otherwise the row's own saved pick.
  const valueOf = useCallback(
    (row: FormStructureField, columnKey: string) =>
      drafts[`${row.id}:${columnKey}`] ?? savedValueOf(row, columnKey),
    [drafts, savedValueOf],
  );
  const onValueCommit = useCallback(
    (rowId: string, columnKey: string, optionId: string) =>
      setDrafts((d) => ({ ...d, [`${rowId}:${columnKey}`]: optionId })),
    [],
  );

  // Only the cells that actually CHANGED, and only for rows still loaded —
  // a draft's row supplies the formId and elementGlobalId the payload needs.
  const pending = useMemo<FieldMetadataPick[]>(() => {
    const byId = new Map(items.map((r) => [r.id, r]));
    const out: FieldMetadataPick[] = [];
    for (const [k, optionId] of Object.entries(drafts)) {
      const sep = k.indexOf(':');
      const rowId = k.slice(0, sep);
      const columnKey = k.slice(sep + 1);
      const row = byId.get(rowId);
      if (!row) continue;
      if (optionId === savedValueOf(row, columnKey)) continue;
      if (columnKey === TOPIC_COLUMN_KEY) {
        out.push({
          kind: FormMetadataType.Topic,
          formId: row.formId,
          elementGlobalId: row.id,
          topicSetId: optionId || null,
        });
      } else {
        const qs = enabledSets.find(
          (q) => questionSetColumnKey(q.sqlColumn.toLowerCase()) === columnKey,
        );
        if (qs)
          out.push({
            kind: FormMetadataType.QuestionSet,
            formId: row.formId,
            elementGlobalId: row.id,
            formSetId: qs.id,
            formSetOptionId: optionId || null,
          });
      }
    }
    return out;
  }, [drafts, items, savedValueOf, enabledSets]);

  // Which columns the user added or dropped since the baseline — the same delta
  // the meta tab sends, measured against what the server actually holds.
  const { added: addedFormSetIds, removed: removedFormSetIds } = useMemo(
    () => setsDelta(enabledSetIds, savedSetIds),
    [enabledSetIds, savedSetIds],
  );

  // Catalogues must be in: an unresolved title reads as an empty cell, and
  // saving that would CLEAR an assignment the user never touched.
  const dirty =
    cataloguesReady &&
    (pending.length > 0 || addedFormSetIds.length > 0 || removedFormSetIds.length > 0);

  const runSave = async () => {
    try {
      await updateFieldMetadata({
        metadata: pending,
        addedFormSetIds,
        removedFormSetIds,
      }).unwrap();
      // The invalidated STRUCTURE tag refetches and the rows come back carrying
      // these values, so the cells read them from the server from here on.
      setDrafts({});
      // Advance the baseline locally too: the refetch re-derives it from the
      // rows, but until it lands a stale one would leave Save enabled with a
      // duplicate add/remove one click away.
      setSavedSetIds(new Set(enabledSetIds));
      toast.success(t('data_setup_sets_saved'));
    } catch (err) {
      // Drafts untouched, so the same payload can simply be retried.
      toast.error(saveErrorMsg(err, t('error_generic')));
    }
  };

  const columns = useFormStructureColumns({
    topics,
    questionSets: enabledSets,
    valueOf,
    onValueCommit,
  });
  // The field title is the row's subject: hiding it would leave rows with
  // nothing to read.
  // Every column visible by default: this table's purpose is to show what the
  // warehouse row will hold, so hiding part of it by default would misrepresent
  // that. It is wide — the table hugs and scrolls, and the Columns menu is how
  // a reader narrows it.
  const { visibleColumns, menuProps } = useColumnVisibility(columns, {
    locked: ['fieldTitle'],
  });

  return (
    <>
      <div className='data-setup-page__toolbar'>
        {/* The toolbar is space-between, so the trailing group needs something
            to be spaced FROM — a lone child would sit at the start instead.
            The lead is empty because this tab's search and Filter are waiting
            on a contract. */}
        <div className='data-setup-page__toolbar-lead' />
        <div className='data-setup-page__toolbar-trail'>
          {/* Always drawn, unlike the meta tab's — Topics is an always-on
              column, so this Save is never a control with nothing to act on. */}
          <Button iconStart='check' onClick={runSave} loading={saving} disabled={!dirty}>
            {t('button_save')}
          </Button>
          <Button variant='secondary' iconStart='table-2' onClick={() => setExportOpen(true)}>
            {t('data_setup_structure_export_title')}
          </Button>
          <Button variant='secondary' iconStart='layers' onClick={() => setManageSetsOpen(true)}>
            {t('data_setup_sets_title')}
            {enabledSetIds.size > 0 && (
              <span className='data-setup-page__sets-count'>{enabledSetIds.size}</span>
            )}
          </Button>
          <DensityToggle value={density} onChange={onDensity} />
          <ColumnsMenu {...menuProps} />
        </div>
      </div>

      <Card padded={false} className='data-setup-page__panel'>
        <DataTable
          columns={visibleColumns}
          rows={items}
          rowKey={(f) => f.id}
          density={density}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          // Eleven columns, two of them 24-char ObjectIds. Below this the three
          // fr columns sit at their floors and the table scrolls, which is the
          // intended behaviour.
          minWidth={2800}
          empty={
            <>
              <Icon name='table-2' size={26} aria-hidden='true' />
              {t('data_setup_structure_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          limits={[10, 20, 50]}
          onPage={setPage}
          onLimit={onLimit}
          perPageLabel={t('data_setup_items_per_page')}
          rangeText={t('data_setup_structure_range', { from, to, total })}
        />
      </Card>

      {/* No header icon, per the design — the SQL settings call site passes one,
          this one doesn't. */}
      <ManageSetsModal
        open={manageSetsOpen}
        onClose={() => setManageSetsOpen(false)}
        sets={questionSets}
        loading={questionSetsLoading}
        error={questionSetsError}
        enabled={enabledSetIds}
        onApply={(next) => applySets(next, questionSets)}
      />

      <ExportSettingsDrawer
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        sections={FORM_STRUCTURE_SECTIONS}
        title={t('data_setup_structure_export_title')}
        subtitle={t('data_setup_structure_export_subtitle')}
      />
    </>
  );
};

export default FormStructureTab;
